import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notificationsClient } from '../api'
import type {
  NotificationPreference,
  SetNotifPrefDto,
  NotificationChannel,
  NotificationTopic,
  ListNotifPrefsDto,
} from '@sphere/notification/domain'

const QUERY_KEY = ['notification', 'preferences'] as const

export function useNotifPrefs(filters?: ListNotifPrefsDto) {
  return useQuery({
    queryKey: [...QUERY_KEY, filters],
    queryFn: () => notificationsClient.getAll(filters),
    staleTime: 5 * 60 * 1000,
  })
}

export function useNotifPref(
  channel: NotificationChannel,
  topic: NotificationTopic
) {
  return useQuery({
    queryKey: [...QUERY_KEY, channel, topic],
    queryFn: () => notificationsClient.getOne(channel, topic),
    enabled: !!channel && !!topic,
  })
}

export function useSetNotifPref() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dto: SetNotifPrefDto) => notificationsClient.set(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

export function useEnableChannel() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (channel: NotificationChannel) =>
      notificationsClient.enableChannel(channel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

export function useDisableChannel() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (channel: NotificationChannel) =>
      notificationsClient.disableChannel(channel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

export function useEnableTopic() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (topic: NotificationTopic) =>
      notificationsClient.enableTopic(topic),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

export function useDisableTopic() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (topic: NotificationTopic) =>
      notificationsClient.disableTopic(topic),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

export function useDeleteAllNotifPrefs() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => notificationsClient.deleteAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

export function useIsNotifEnabled(
  channel: NotificationChannel,
  topic: NotificationTopic
): boolean {
  const { data } = useNotifPrefs()
  const pref = data?.find((p) => p.channel === channel && p.topic === topic)
  return pref?.enabled ?? true
}

export function useNotifPrefsByChannel(): Record<NotificationChannel, NotificationPreference[]> {
  const { data } = useNotifPrefs()

  const byChannel = data?.reduce(
    (acc, pref) => {
      if (!acc[pref.channel]) {
        acc[pref.channel] = []
      }
      acc[pref.channel].push(pref)
      return acc
    },
    {} as Record<NotificationChannel, NotificationPreference[]>
  )

  return byChannel ?? {} as Record<NotificationChannel, NotificationPreference[]>
}
